import axios from "axios";
import apiCache from "../utils/apiCache";
import { API_BASE_URL } from "../apiConstants";
import errorService from "./errorService.js";
import performanceService from "./performanceService.js";
import {
  apiCall,
  handleApiError as enhancedHandleApiError,
} from "../utils/apiErrorHandler";
import { getAuthHeader as _getAuthHeader } from "./authHeader";
import { toSnake, toCamel } from "../utils/caseConverter";

/**
 * Common API utility functions for authentication, caching, and error handling
 */

// Default cache TTL values by method
const DEFAULT_CACHE_TTL = {
  GET_ALL: 5 * 60 * 1000, // 5 minutes
  GET_BY_ID: 5 * 60 * 1000, // 5 minutes
};

// Default request timeouts by operation type
const DEFAULT_TIMEOUT = 30000; // 30 seconds
const TIMEOUT_CONFIG = {
  quick: 10000, // 10 seconds for quick queries
  normal: 30000, // 30 seconds for normal operations
  upload: 120000, // 2 minutes for file uploads
  download: 60000, // 1 minute for downloads
  batch: 180000, // 3 minutes for batch operations
};

export { TIMEOUT_CONFIG };

// Get the standard cookie-backed request configuration.
export const getAuthHeader = () => _getAuthHeader();


// Configure axios with default configurations
export const configureAxios = () => {
  // All requests send cookies (nd_access/nd_refresh httpOnly — T201)
  axios.defaults.withCredentials = true;

  // Request interceptor — convert camelCase payload to snake_case for Phoenix
  axios.interceptors.request.use(
    (config) => {
      // Start performance timer for API calls
      const timer = performanceService.timeApiCall(
        config.url,
        config.method?.toUpperCase(),
      );
      config.metadata = { timer, startTime: Date.now() };
      // Multipart boundaries must come from the browser, not the JSON auth defaults.
      if (config.data instanceof FormData) {
        config.headers.delete("Content-Type");
      } else if (config.data && typeof config.data === "object") {
        config.data = toSnake(config.data);
      }
      return config;
    },
    (error) => {
      errorService.logError(error, {
        type: "api",
        component: "axios_request_interceptor",
        action: "request_setup",
      });
      return Promise.reject(error);
    },
  );

  // Response interceptor — convert snake_case response to camelCase for pages
  axios.interceptors.response.use(
    (response) => {
      // End performance timer
      if (response.config.metadata?.timer) {
        const responseSize = JSON.stringify(response.data).length;
        response.config.metadata.timer.end(true, responseSize);
      }
      // Convert incoming snake_case keys to camelCase
      if (response.data && typeof response.data === "object") {
        response.data = toCamel(response.data);
      }
      return response;
    },
    async (error) => {
      // End performance timer for failed requests
      if (error.config?.metadata?.timer) {
        error.config.metadata.timer.end(false, 0);
      }

      // 401 on a session-probe endpoint (/auth/me) while on a public path is expected
      // for unauthenticated guests — do not treat it as an error worth persisting.
      const PUBLIC_PATHS = new Set(["/", "/login", "/register", "/reset-password", "/auth/oauth/callback"]);
      const isSessionProbe401 =
        error.response?.status === 401 &&
        PUBLIC_PATHS.has(window.location.pathname);

      // A caller can mark a status as an answer it expects (an unset setting
      // is a 404): that is not an error worth logging.
      const isExpected = error.config?.expectedStatuses?.includes(error.response?.status);

      if (!isSessionProbe401 && !isExpected) {
        errorService.logError(error, {
          type: "api",
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          component: "axios_response_interceptor",
        });
      }

      // Handle 401 errors — token refresh is handled by authApi.js interceptor
      // This interceptor only logs; the authApi interceptor does the actual refresh

      return Promise.reject(error);
    },
  );
};

// Standardized API error handling (legacy - use enhancedHandleApiError for new code)
export const handleApiError = (error, operation) => {
  return enhancedHandleApiError(error, operation);
};

/**
 * Make an API request with caching support and enhanced error handling
 *
 * @param {Object} options - Request options
 * @returns {Promise<any>} - Response data
 */
export const makeRequest = async (options) => {
  const {
    method = "GET",
    url,
    params,
    data,
    cache = true,
    cacheTTL,
    forceRefresh = false,
    timeout = DEFAULT_TIMEOUT,
    additionalHeaders = {},
    retries = 3,
    retryDelay = 1000,
  } = options;

  const isReadOperation = method === "GET";

  // Authentication is carried by httpOnly cookies.
  const authConfig = getAuthHeader();
  const headers = { ...authConfig.headers, ...additionalHeaders };

  // Check cache for GET requests if caching is enabled and not forcing refresh
  if (cache && isReadOperation && !forceRefresh) {
    const cacheKey = apiCache.generateKey(url, params);
    const cachedData = apiCache.get(cacheKey);

    if (cachedData !== null) {
      return cachedData;
    }
  }

  // Create AbortController for request cancellation
  const controller = new AbortController();

  // Set up timeout handler
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeout);

  try {
    // Use enhanced error handling with retry logic
    const result = await apiCall(
      async () => {
        // Make the request with AbortController signal
        const response = await axios({
          method,
          url,
          params,
          data,
          headers,
          withCredentials: true,
          timeout,
          signal: controller.signal,
        });

        // Cache GET responses if caching is enabled
        if (cache && isReadOperation) {
          const cacheKey = apiCache.generateKey(url, params);
          const ttl = cacheTTL || DEFAULT_CACHE_TTL.GET_ALL;
          apiCache.set(cacheKey, response.data, ttl);
        }

        return response.data;
      },
      {
        context: { operation: `${method} ${url}` },
        retries,
        retryDelay,
        timeout,
        controller,
      },
    );

    clearTimeout(timeoutId);
    return result;
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle abort errors specifically
    if (error.name === "AbortError" || error.code === "ECONNABORTED") {
      const timeoutError = new Error(`Request timeout after ${timeout}ms`);
      timeoutError.code = "TIMEOUT";
      timeoutError.timeout = timeout;
      throw timeoutError;
    }

    throw error;
  }
};

/**
 * Create an API service for a specific resource type
 *
 * @param {string} resourcePath - Resource path (e.g., 'contacts', 'deals')
 * @param {Object} options - Service options
 * @returns {Object} - API service methods
 */
export const createApiService = (resourcePath, options = {}) => {
  if (!resourcePath) {
    throw new Error("Resource path is required");
  }

  // Parse options with defaults
  const {
    baseUrl = API_BASE_URL,
    cacheTTL = {},
    timeout = DEFAULT_TIMEOUT,
    cacheEnabled = true,
    retryCount = 0,
  } = options;

  // Create API base URL for this resource
  const apiUrl = `${baseUrl}/${resourcePath}`;

  /**
   * Invalidate cache based on operation type and resource ID
   *
   * @param {string} operation - Operation type (create, update, delete)
   * @param {string|number} id - Resource ID (for update/delete)
   */
  const invalidateCache = (operation, id = null) => {
    if (!cacheEnabled) return;

    // Always invalidate the collection
    apiCache.invalidateByUrl(apiUrl);

    // Invalidate the specific resource for updates and deletes
    if (id && (operation === "update" || operation === "delete")) {
      apiCache.invalidate(`${apiUrl}/${id}`);
    }
  };

  // Return the API service object with standard CRUD methods
  return {
    /**
     * Get all resources with pagination, search, and filtering
     *
     * @param {Object} params - Query parameters
     * @param {Object} options - Request options
     * @returns {Promise<Object>} - Response with data and pagination info
     */
    getAll: async (params = {}, options = {}) => {
      return makeRequest({
        method: "GET",
        url: apiUrl,
        params,
        cache: cacheEnabled && !options.noCache,
        cacheTTL:
          options.cacheTTL || cacheTTL.GET_ALL || DEFAULT_CACHE_TTL.GET_ALL,
        forceRefresh: options.forceRefresh,
        timeout: options.timeout || timeout,
      });
    },

    /**
     * Get a resource by ID
     *
     * @param {string|number} id - Resource ID
     * @param {Object} options - Request options
     * @returns {Promise<Object>} - Resource data
     */
    getById: async (id, options = {}) => {
      return makeRequest({
        method: "GET",
        url: `${apiUrl}/${id}`,
        cache: cacheEnabled && !options.noCache,
        cacheTTL:
          options.cacheTTL || cacheTTL.GET_BY_ID || DEFAULT_CACHE_TTL.GET_BY_ID,
        forceRefresh: options.forceRefresh,
        timeout: options.timeout || timeout,
      });
    },

    /**
     * Create a new resource
     *
     * @param {Object} data - Resource data
     * @returns {Promise<Object>} - Created resource
     */
    create: async (data) => {
      const response = await makeRequest({
        method: "POST",
        url: apiUrl,
        data,
        cache: false,
        timeout,
      });

      invalidateCache("create");
      return response;
    },

    /**
     * Update a resource
     *
     * @param {string|number} id - Resource ID
     * @param {Object} data - Resource data
     * @returns {Promise<Object>} - Updated resource
     */
    update: async (id, data) => {
      const response = await makeRequest({
        method: "PUT",
        url: `${apiUrl}/${id}`,
        data,
        cache: false,
        timeout,
      });

      invalidateCache("update", id);
      return response;
    },

    /**
     * Delete a resource
     *
     * @param {string|number} id - Resource ID
     * @returns {Promise<Object>} - Response data
     */
    delete: async (id) => {
      const response = await makeRequest({
        method: "DELETE",
        url: `${apiUrl}/${id}`,
        cache: false,
        timeout,
      });

      invalidateCache("delete", id);
      return response;
    },

    /**
     * Refresh cache for this resource type
     *
     * @param {string|number} id - Specific resource ID to refresh, or null for all
     */
    refreshCache: (id = null) => {
      if (id) {
        apiCache.invalidate(`${apiUrl}/${id}`);
      } else {
        apiCache.invalidateByUrl(apiUrl);
      }
    },

    /**
     * Custom API request
     *
     * @param {string} method - HTTP method
     * @param {string} endpoint - API endpoint
     * @param {Object} requestOptions - Request options
     * @returns {Promise<any>} - Response data
     */
    request: async (method, endpoint, requestOptions = {}) => {
      const url = endpoint.startsWith("/")
        ? `${baseUrl}${endpoint}`
        : `${apiUrl}/${endpoint}`;

      return makeRequest({
        method,
        url,
        params: requestOptions.params,
        data: requestOptions.data,
        cache:
          requestOptions.cache !== undefined
            ? requestOptions.cache
            : method === "GET" && cacheEnabled,
        cacheTTL: requestOptions.cacheTTL,
        forceRefresh: requestOptions.forceRefresh,
        timeout: requestOptions.timeout || timeout,
        additionalHeaders: requestOptions.headers,
      });
    },
  };
};

/**
 * Create a timeout wrapper for axios requests
 * @param {Object} config - Axios config
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Object} - Enhanced axios config with timeout handling
 */
export const withTimeout = (config, timeout = DEFAULT_TIMEOUT) => {
  const controller = new AbortController();

  return {
    ...config,
    timeout,
    signal: controller.signal,
    cancelToken: undefined, // Remove deprecated cancelToken if present
    controller, // Expose controller for manual cancellation
  };
};

/**
 * Get timeout value based on operation type
 * @param {string} operationType - Type of operation
 * @returns {number} - Timeout in milliseconds
 */
export const getTimeoutForOperation = (operationType) => {
  return TIMEOUT_CONFIG[operationType] || TIMEOUT_CONFIG.normal;
};


export default {
  getAuthHeader,
  configureAxios,
  handleApiError,
  makeRequest,
  createApiService,
  withTimeout,
  getTimeoutForOperation,
  TIMEOUT_CONFIG,
  // Expose cache interface for direct access if needed
  cache: {
    get: apiCache.get.bind(apiCache),
    set: apiCache.set.bind(apiCache),
    invalidate: apiCache.invalidate.bind(apiCache),
    clear: apiCache.clear.bind(apiCache),
    getStats: apiCache.getStats.bind(apiCache),
  },
};
